import { useNavigate } from "@Light/utils/context";
import { createContext, useContext, useEffect, useLayoutEffect } from "react";
export type OnBack = () => void;
export type HeaderState = {
  onBack: OnBack | undefined;
  setOnBack: (v: OnBack | undefined) => void;
  contentHeight: number | undefined;
  setContentHeight: (v: number | undefined) => void;
  divider: boolean;
  setDivider: (v: boolean) => void;
  titleText: React.ReactNode | undefined;
  setTitleText: (v: React.ReactNode | undefined) => void;
  leftButton: React.ReactNode | undefined;
  setLeftButton: (v: React.ReactNode | undefined) => void;
  rightButton: React.ReactNode | undefined;
  setRightButton: (v: React.ReactNode | undefined) => void;
  floating: boolean;
  setFloating: (v: boolean) => void;
  compact: boolean;
  setCompact: (v: boolean) => void;
};
export const HeaderContext = createContext<HeaderState | undefined>(undefined);
export function useHeader() {
  const header = useContext(HeaderContext);
  const sizeProps = (() => {
    if (!header?.contentHeight) {
      return {
        style: {
          height: "100%",
        },
      };
    }
    return {
      style: { height: `${header.contentHeight}px` },
    };
  })();
  return {
    sizeProps,
  };
}

export function useHeaderBack(onBack?: OnBack) {
  const setOnBack = useContext(HeaderContext)?.setOnBack;
  useEffect(() => {
    if (!setOnBack) return;
    setOnBack(() => onBack);
    return () => {
      setOnBack(undefined);
    };
  }, [setOnBack, onBack]);
}

export function useHeaderBackNavigate(route: string | undefined) {
  const navigate = useNavigate();
  const setOnBack = useContext(HeaderContext)?.setOnBack;
  useEffect(() => {
    if (!setOnBack || !route) return;
    setOnBack(() => () => {
      navigate(route);
    });
    return () => {
      setOnBack(undefined);
    };
  }, [setOnBack, navigate, route]);
}

export function useHeaderTitle(title: React.ReactNode) {
  const setTitleText = useContext(HeaderContext)?.setTitleText;
  useEffect(() => {
    if (!setTitleText) return;
    setTitleText(title);
    return () => {
      setTitleText(undefined);
    };
  }, [setTitleText, title]);
}

export function useHeaderDivider(divider: boolean) {
  const setDivider = useContext(HeaderContext)?.setDivider;
  useEffect(() => {
    if (!setDivider) return;
    setDivider(divider);
    return () => {
      setDivider(true);
    };
  }, [setDivider, divider]);
}

export function useHeaderLeftButton(leftButton: React.ReactNode) {
  const setLeftButton = useContext(HeaderContext)?.setLeftButton;
  useEffect(() => {
    if (!setLeftButton) return;
    setLeftButton(leftButton);
    return () => {
      setLeftButton(undefined);
    };
  }, [setLeftButton, leftButton]);
}

export function useHeaderRightButton(rightButton: React.ReactNode) {
  const setRightButton = useContext(HeaderContext)?.setRightButton;
  useEffect(() => {
    if (!setRightButton) return;
    setRightButton(rightButton);
    return () => {
      setRightButton(undefined);
    };
  }, [setRightButton, rightButton]);
}

export function useHeaderFloating(floating: boolean) {
  const setFloating = useContext(HeaderContext)?.setFloating;
  useLayoutEffect(() => {
    if (!setFloating) return;
    setFloating(floating);
    return () => {
      setFloating(false);
    };
  }, [setFloating, floating]);
}

export function useHeaderCompact(compact: boolean) {
  const setCompact = useContext(HeaderContext)?.setCompact;
  useLayoutEffect(() => {
    if (!setCompact) return;
    setCompact(compact);
    return () => {
      setCompact(false);
    };
  }, [setCompact, compact]);
}
